import { fromDateTimeLocalValue, isValidEmail, toDateTimeLocalValue } from './events'
import {
  EVENT_PRICE_MAX_CENTS,
  EVENT_PRICE_MIN_CENTS,
  parsePriceInput,
  priceToInput,
} from './eventPrice'

/**
 * Event creation / edition form: option lists, limits, validation and payload
 * building. Pure functions, aligned with the API contract (keeptouch-api src/events).
 */
export const EVENT_CATEGORIES = [
  'networking',
  'conference',
  'workshop',
  'meetup',
  'trade_show',
  'seminar',
  'other',
] as const
export type EventCategory = (typeof EVENT_CATEGORIES)[number]

export const EVENT_VISIBILITIES = ['private', 'public'] as const
export type EventVisibility = (typeof EVENT_VISIBILITIES)[number]

export const EVENT_FORMATS = ['in_person', 'online', 'hybrid'] as const
export type EventFormat = (typeof EVENT_FORMATS)[number]

export const EVENT_LIMITS = {
  nameMin: 2,
  nameMax: 120,
  descriptionMax: 5000,
  locationMax: 200,
  addressMax: 300,
  cityMax: 120,
  urlMax: 500,
  emailMax: 254,
  capacityMax: 100000,
} as const

const HOUR = 60 * 60 * 1000
const DAY = 24 * HOUR

/** Everything the form holds, as the strings typed by the organizer. */
export interface EventFormState {
  name: string
  category: EventCategory | ''
  /** Private by default: nothing is listed unless the organizer chooses public. */
  visibility: EventVisibility
  description: string
  startsAt: string
  endsAt: string
  format: EventFormat
  location: string
  address: string
  city: string
  /** Set when the venue was picked from the search; null when typed by hand. */
  latitude: number | null
  longitude: number | null
  onlineUrl: string
  capacity: string
  /** Ticketing: free by default; `price` is the euros typed by the organizer. */
  isPaid: boolean
  price: string
  registrationClosesAt: string
  coverImageUrl: string
  contactEmail: string
  websiteUrl: string
}

export type EventFormErrorKey =
  | 'name'
  | 'dates'
  | 'order'
  | 'capacity'
  | 'price'
  | 'url'
  | 'email'
  | 'locationRequired'
  | 'onlineUrlRequired'
  | 'deadlinePast'

export type EventFormErrors = Partial<Record<keyof EventFormState, EventFormErrorKey>>

/** Body of `POST /events` / `PATCH /events/:id`. */
export interface EventPayload {
  name: string
  description?: string | null
  category?: EventCategory | null
  visibility?: EventVisibility
  format?: EventFormat
  location?: string | null
  address?: string | null
  city?: string | null
  latitude?: number | null
  longitude?: number | null
  onlineUrl?: string | null
  startsAt: string
  endsAt: string
  registrationClosesAt?: string | null
  capacity?: number | null
  /** Cents; null = free. */
  priceCents?: number | null
  coverImageUrl?: string | null
  websiteUrl?: string | null
  contactEmail?: string | null
  remindersEnabled?: boolean
}

/** Tomorrow 19:00 → 22:00: a sensible default for an evening event. */
export function defaultEventFormState(now: Date = new Date()): EventFormState {
  const start = new Date(now.getTime() + DAY)
  start.setMinutes(0, 0, 0)
  start.setHours(19)
  const end = new Date(start.getTime() + 3 * HOUR)
  return {
    name: '',
    category: '',
    visibility: 'private',
    description: '',
    startsAt: toDateTimeLocalValue(start.toISOString()),
    endsAt: toDateTimeLocalValue(end.toISOString()),
    format: 'in_person',
    location: '',
    address: '',
    city: '',
    latitude: null,
    longitude: null,
    onlineUrl: '',
    capacity: '',
    isPaid: false,
    price: '',
    registrationClosesAt: '',
    coverImageUrl: '',
    contactEmail: '',
    websiteUrl: '',
  }
}

/** Fields of an event as returned by the API (subset used to fill the form). */
export interface EventFormSource {
  name: string
  description: string | null
  category: EventCategory | null
  visibility?: EventVisibility
  format: EventFormat
  location: string | null
  address: string | null
  city: string | null
  latitude: number | null
  longitude: number | null
  onlineUrl: string | null
  startsAt: string
  endsAt: string
  joinClosesAt: string
  capacity: number | null
  priceCents?: number | null
  coverImageUrl: string | null
  websiteUrl: string | null
  contactEmail: string | null
}

export function eventToFormState(event: EventFormSource): EventFormState {
  return {
    name: event.name,
    category: event.category ?? '',
    visibility: event.visibility ?? 'private',
    description: event.description ?? '',
    startsAt: toDateTimeLocalValue(event.startsAt),
    endsAt: toDateTimeLocalValue(event.endsAt),
    format: event.format ?? 'in_person',
    location: event.location ?? '',
    address: event.address ?? '',
    city: event.city ?? '',
    latitude: event.latitude ?? null,
    longitude: event.longitude ?? null,
    onlineUrl: event.onlineUrl ?? '',
    capacity: event.capacity ? String(event.capacity) : '',
    isPaid: event.priceCents !== null && event.priceCents !== undefined,
    price: priceToInput(event.priceCents),
    registrationClosesAt: toDateTimeLocalValue(event.joinClosesAt),
    coverImageUrl: event.coverImageUrl ?? '',
    contactEmail: event.contactEmail ?? '',
    websiteUrl: event.websiteUrl ?? '',
  }
}

export function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value.trim())
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

export const formNeedsVenue = (format: EventFormat) => format !== 'online'
export const formNeedsOnlineUrl = (format: EventFormat) => format !== 'in_person'

export interface ValidateOptions {
  /** Form state loaded from the API when editing; used to detect a changed deadline. */
  initial?: EventFormState
  now?: Date
}

export interface ValidationResult {
  errors: EventFormErrors
  payload: EventPayload | null
}

const emptyToNull = (value: string): string | null => value.trim() || null

/**
 * Validates the form like the API does and builds the request body. On edit the
 * registration deadline is only sent when the organizer touched it: it is
 * pre-filled with the automatic "end + 7 days" value, and sending it back would
 * freeze it.
 */
export function validateEventForm(
  values: EventFormState,
  { initial, now = new Date() }: ValidateOptions = {},
): ValidationResult {
  const errors: EventFormErrors = {}
  const name = values.name.trim()
  if (name.length < EVENT_LIMITS.nameMin) errors.name = 'name'

  const startsAt = fromDateTimeLocalValue(values.startsAt)
  const endsAt = fromDateTimeLocalValue(values.endsAt)
  if (!startsAt) errors.startsAt = 'dates'
  if (!endsAt) errors.endsAt = 'dates'
  if (startsAt && endsAt && new Date(endsAt) <= new Date(startsAt)) errors.endsAt = 'order'

  const capacity = values.capacity.trim()
  if (capacity) {
    const isInteger = /^[1-9]\d*$/.test(capacity)
    if (!isInteger || Number(capacity) > EVENT_LIMITS.capacityMax) errors.capacity = 'capacity'
  }

  let priceCents: number | null = null
  if (values.isPaid) {
    priceCents = parsePriceInput(values.price)
    if (
      priceCents === null ||
      priceCents < EVENT_PRICE_MIN_CENTS ||
      priceCents > EVENT_PRICE_MAX_CENTS
    ) {
      errors.price = 'price'
      priceCents = null
    }
  }

  if (formNeedsVenue(values.format) && !values.location.trim()) errors.location = 'locationRequired'
  const onlineUrl = values.onlineUrl.trim()
  if (formNeedsOnlineUrl(values.format)) {
    if (!onlineUrl) errors.onlineUrl = 'onlineUrlRequired'
    else if (!isHttpUrl(onlineUrl)) errors.onlineUrl = 'url'
  }

  for (const key of ['coverImageUrl', 'websiteUrl'] as const) {
    const value = values[key].trim()
    if (value && !isHttpUrl(value)) errors[key] = 'url'
  }
  const contactEmail = values.contactEmail.trim()
  if (contactEmail && !isValidEmail(contactEmail)) errors.contactEmail = 'email'

  const deadlineChanged = !initial || values.registrationClosesAt !== initial.registrationClosesAt
  let registrationClosesAt: string | null | undefined
  if (deadlineChanged) {
    const deadline = values.registrationClosesAt.trim()
    if (!deadline) registrationClosesAt = initial ? null : undefined
    else {
      registrationClosesAt = fromDateTimeLocalValue(deadline)
      if (!registrationClosesAt) errors.registrationClosesAt = 'dates'
      else if (new Date(registrationClosesAt) <= now) errors.registrationClosesAt = 'deadlinePast'
    }
  }

  if (Object.keys(errors).length > 0 || !startsAt || !endsAt) return { errors, payload: null }

  const { format } = values
  const payload: EventPayload = {
    name,
    description: emptyToNull(values.description),
    category: values.category || null,
    visibility: values.visibility,
    format,
    location: formNeedsVenue(format) ? emptyToNull(values.location) : null,
    address: formNeedsVenue(format) ? emptyToNull(values.address) : null,
    city: formNeedsVenue(format) ? emptyToNull(values.city) : null,
    latitude: formNeedsVenue(format) ? values.latitude : null,
    longitude: formNeedsVenue(format) ? values.longitude : null,
    onlineUrl: formNeedsOnlineUrl(format) ? onlineUrl : null,
    startsAt,
    endsAt,
    capacity: capacity ? Number(capacity) : null,
    priceCents,
    coverImageUrl: emptyToNull(values.coverImageUrl),
    websiteUrl: emptyToNull(values.websiteUrl),
    contactEmail: emptyToNull(contactEmail),
  }
  if (registrationClosesAt !== undefined) payload.registrationClosesAt = registrationClosesAt
  return { errors, payload }
}
